import { useState, useEffect } from "react";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, Calendar, Check, X, ChevronUp, ChevronDown } from "lucide-react";
import { getOrgForUser } from "@/lib/orgBilling";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ColorPicker } from "@/components/admin/ColorPicker";
import { EntryMethodEditor, EntryMethodInput } from "@/components/admin/EntryMethodEditor";
import { LifecycleContestSettings } from "@/components/admin/LifecycleSettings";
import { TermsFieldsAccordion, TermsFieldsData } from "@/components/admin/TermsFieldsAccordion";
import { TermsPreviewButton } from "@/components/admin/TermsPreviewButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { Json } from "@/integrations/supabase/types";
import { normalizeHeroAspect, HERO_ASPECT_OPTIONS, DEFAULT_HERO_ASPECT, type HeroAspect } from "@/lib/heroAspect";
import { uploadOrgAsset, getSignedUrl, normalizeImageFileExt } from "@/lib/storage";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, Upload, X as XIcon, Image as ImageIcon } from "lucide-react";
import {
  ContestEntryField,
  CustomInputType,
  DateRange,
  DEFAULT_ENTRY_METHODS_HEADING,
  FieldValidationRules,
  persistContestEntryFields,
  persistEntryMethodsHeading,
  validateEntryFieldsForSave,
} from "@/lib/entryFields";
import { isSupersededEntryMethod } from "@/lib/entryMethods";

interface ContestTheme {
  headingColor: string;
  buttonBackground: string;
  buttonText: string;
  background: string;
  [key: string]: string;
}

const defaultTheme: ContestTheme = {
  headingColor: "#0d9488",
  buttonBackground: "#f97316",
  buttonText: "#ffffff",
  background: "#f8fafc",
};

const defaultTermsFields: TermsFieldsData = {
  sponsorName: "",
  sponsorAddress: "",
  eligibleRegions: "",
  minimumAge: "18",
  excludedStates: "",
  startDate: "",
  startTime: "",
  prizeDescription: "",
  prizeArv: "",
  prizeQuantity: "One (1)",
  totalPrizeValue: "",
  winnerNotificationMethod: "email",
  winnerNotificationTimeframe: "7 days",
  responseWindow: "48 hours",
  winnerSelectionDate: "",
  governingState: "",
  supportEmail: "",
  mailInAddress: "",
  entryLimit: "",
  includeMarketingOptIn: "no",
};

export default function ContestForm() {
  const { slug: editSlug } = useParams<{ slug?: string }>();
  // Use location to be absolutely sure about "create" vs "edit" mode
  const location = useLocation();
  const isCreateMode = location.pathname.endsWith("/new");
  const isEditing = !!editSlug && !isCreateMode;

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [referralBonus, setReferralBonus] = useState(1);
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [endTime, setEndTime] = useState("23:59");
  const [theme, setTheme] = useState<ContestTheme>(defaultTheme);
  const [logoUrl, setLogoUrl] = useState(""); // Legacy
  const [logoPath, setLogoPath] = useState("");
  const [useOrgLogo, setUseOrgLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoSignedUrl, setLogoSignedUrl] = useState("");

  const [heroImageUrl, setHeroImageUrl] = useState(""); // Legacy
  const [heroPath, setHeroPath] = useState("");
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [heroSignedUrl, setHeroSignedUrl] = useState("");

  const [heroImageAspect, setHeroImageAspect] = useState<HeroAspect>(DEFAULT_HERO_ASPECT);
  const [hidePoweredByBranding, setHidePoweredByBranding] = useState(false);
  const [useCustomTerms, setUseCustomTerms] = useState(false);
  const [requireTermsAcceptance, setRequireTermsAcceptance] = useState(false);
  const [customTermsText, setCustomTermsText] = useState("");
  const [showCustomTermsWarning, setShowCustomTermsWarning] = useState(false);
  const [termsFields, setTermsFields] = useState<TermsFieldsData>(defaultTermsFields);
  const [entryMethods, setEntryMethods] = useState<EntryMethodInput[]>([
    {
      key: "visit",
      label: "Visit Website",
      description: "Visit our site to earn entries",
      entriesAwarded: 1,
      actionUrl: "",
      iconType: "library",
      iconKey: "link",
      iconUrl: null,
      isEnabled: true,
    },
  ]);
  const [entryFields, setEntryFields] = useState<ContestEntryField[]>([]);
  const [showEntryMethodsHeading, setShowEntryMethodsHeading] = useState(false);
  const [entryMethodsHeading, setEntryMethodsHeading] = useState(DEFAULT_ENTRY_METHODS_HEADING);
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState<"idle" | "creating" | "redirecting">("idle");
  const [legalConfirmed, setLegalConfirmed] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check auth
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        navigate("/admin");
        return;
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  // Fetch existing contest if editing
  const { data: existingContest, isLoading: isLoadingContest, isFetching: isFetchingContest } = useQuery({
    queryKey: ["admin-contest-edit", editSlug],
    queryFn: async () => {
      if (!editSlug) return null;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data: membership, error: memErr } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", session.user.id)
        .limit(1)
        .maybeSingle();

      if (memErr || !membership) {
        throw new Error("Organization not found");
      }

      const { data: contest, error } = await supabase
        .from("contests")
        .select("*")
        .eq("slug", editSlug)
        .eq("organization_id", membership.organization_id)
        .maybeSingle();

      if (error) throw error;
      if (!contest) throw new Error("Contest not found");

      const { data: methods } = await supabase
        .from("entry_methods")
        .select("*")
        .eq("contest_id", contest.id)
        .order("sort_order", { ascending: true });

      return { contest, methods: methods || [] };
    },
    enabled: isEditing && !isCheckingAuth,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Fetch Org Plan for Slug Rules
  const { data: org, isLoading: isLoadingOrg } = useQuery({
    queryKey: ["contest-form-org"],
    queryFn: () => getOrgForUser(supabase),
    enabled: !isCheckingAuth
  });

  // Prefill Terms Defaults from Org Settings
  useEffect(() => {
    if (!org) return;

    setTermsFields(prev => {
      const next = { ...prev };

      // Sponsor Name: Org Setting -> Org Name -> Empty
      if (!next.sponsorName) {
        next.sponsorName = org.sponsor_name || org.name;
      }

      // Sponsor Address: Org Setting -> Empty
      if (!next.sponsorAddress) {
        next.sponsorAddress = org.sponsor_address || "";
      }

      // Support Email: Org Setting -> Empty (No fallback to user email or hardcoded)
      if (!next.supportEmail) {
        next.supportEmail = org.support_email || "";
      }

      return next;
    });
  }, [org]); // Run when org loads

  const isFreePlan = !org || org.plan_key === 'free';
  const orgLogoPath = org?.logo_path; // From org query
  const [orgLogoSignedUrl, setOrgLogoSignedUrl] = useState("");

  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [showIncompleteTermsModal, setShowIncompleteTermsModal] = useState(false);

  // Help Email Config
  const [showHelpEmail, setShowHelpEmail] = useState(true);
  const [helpEmail, setHelpEmail] = useState("");
  const [helpEmailError, setHelpEmailError] = useState("");

  // Total Entries Config
  const [showTotalEntries, setShowTotalEntries] = useState(false);

  // Initial Form Entry Config
  const [initialFormCountsAsEntry, setInitialFormCountsAsEntry] = useState(false);

  // Realtime Slug Check
  useEffect(() => {
    if (isFreePlan || !slug) {
      setSlugStatus("idle");
      return;
    }

    // If editing and slug text matches current saved slug, it's valid (available to self)
    if (isEditing && existingContest?.contest?.slug === slug) {
      setSlugStatus("available");
      return;
    }

    setSlugStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('slug-check', {
          body: { slug }
        });

        if (error) throw error;

        if (data?.available) {
          setSlugStatus("available");
        } else {
          setSlugStatus("taken");
          // Auto-suffix collision resolution
          const suffix = Math.floor(100000 + Math.random() * 900000);
          const newSlug = `${slug}-${suffix}`;
          setSlug(newSlug);
          toast({
            title: "Slug unavailable",
            description: `Updated to ${newSlug} to ensure uniqueness.`,
          });
        }
      } catch (e) {
        console.error("Slug check failed", e);
        setSlugStatus("idle");
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [slug, isFreePlan, isEditing, existingContest]);


  // Populate form when editing - only once when data loads
  useEffect(() => {
    if (existingContest && !isFormInitialized && !isFetchingContest) {
      const { contest, methods } = existingContest;
      const contestAny = contest as any;

      setName(contest.name);
      setSlug(contest.slug);
      setDescriptionHtml(contest.description_html || "");
      setReferralBonus(contest.referral_bonus_entries || 1);
      if (contest.ends_at) {
        const dt = new Date(contest.ends_at);
        setEndDate(dt);
        setEndTime(format(dt, "HH:mm"));
      }
      setTheme((contest.theme_json as unknown as ContestTheme) || defaultTheme);
      setLogoUrl(contest.logo_url || "");
      setLogoPath(contestAny.logo_path || "");
      setUseOrgLogo(contestAny.use_org_logo || false);

      setHeroImageUrl(contest.hero_image_url || "");
      setHeroPath(contestAny.hero_image_path || "");
      setHidePoweredByBranding(contestAny.hide_powered_by_branding || false);

      // Trigger signed URL fetch (effects will handle it based on path changes)



      // Load terms variables
      const savedTerms = contest.terms_variables_json as unknown as TermsFieldsData | null;
      if (savedTerms) {
        setTermsFields({
          ...defaultTermsFields,
          ...savedTerms,
        });
      }

      // Load custom terms settings
      setUseCustomTerms(contestAny.use_custom_terms || false);
      setCustomTermsText(contestAny.custom_terms_text || "");
      setRequireTermsAcceptance(contestAny.require_terms_acceptance || false);
      setShowHelpEmail(contestAny.show_help_email ?? true);
      setHelpEmail(contestAny.help_email || "");
      setShowTotalEntries(contestAny.show_total_entries || false);
      setInitialFormCountsAsEntry(contestAny.initial_form_counts_as_entry || false);
      setShowEntryMethodsHeading(
        contestAny.show_entry_methods_heading === true ||
        contestAny.show_entry_methods_heading === "true" ||
        contestAny.show_entry_methods_heading === "t"
      );
      setEntryMethodsHeading(contestAny.entry_methods_heading || DEFAULT_ENTRY_METHODS_HEADING);

      if (methods.length > 0) {
        const activeMethods = methods.filter((method: { is_enabled?: boolean | null }) => method.is_enabled !== false);
        const editorMethods = activeMethods.filter((method: { id?: string; key: string; label: string }) =>
          method.key === "signup" || !isSupersededEntryMethod(method, activeMethods)
        );
        setEntryMethods(editorMethods.map((m: any) => ({
          id: m.id,
          key: m.key,
          label: m.label,
          description: m.description || "",
          entriesAwarded: m.entries_awarded || 1,
          actionUrl: m.action_url || "",
          iconType: m.icon_type,
          iconKey: m.icon_key,
          iconUrl: m.icon_url,
          iconPath: m.icon_path,
          isEnabled: m.is_enabled,
        })));
      }

      setIsFormInitialized(true);
    }
  }, [existingContest, isFormInitialized, isFetchingContest]);

  // Load entry fields
  useEffect(() => {
    if (isEditing && existingContest?.contest?.id) {
      supabase
        .from("contest_entry_fields")
        .select("*")
        .eq("contest_id", existingContest.contest.id)
        .order("sort_order")
        .then(({ data }) => {
          if (data) {
            setEntryFields(data.map((field: any) => ({
              ...field,
              validation_rules: field.validation_rules && typeof field.validation_rules === "object"
                ? field.validation_rules
                : {},
            })));
          }
        });
    }
  }, [isEditing, existingContest?.contest?.id]);

  // Effect to load Org Logo Signed URL
  useEffect(() => {
    if (orgLogoPath) {
      getSignedUrl(orgLogoPath).then(url => {
        if (url) setOrgLogoSignedUrl(url);
      });
    }
  }, [orgLogoPath]);

  // Effect to load Contest Asset Signed URLs
  useEffect(() => {
    // Logo
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoSignedUrl(url); // Preview local file
      return () => URL.revokeObjectURL(url);
    } else if (logoPath) {
      getSignedUrl(logoPath).then(url => { if (url) setLogoSignedUrl(url); });
    } else if (logoUrl) {
      setLogoSignedUrl(logoUrl); // Legacy URL
    } else {
      setLogoSignedUrl("");
    }
  }, [logoPath, logoUrl, logoFile]);

  useEffect(() => {
    // Hero
    if (heroFile) {
      const url = URL.createObjectURL(heroFile);
      setHeroSignedUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (heroPath) {
      getSignedUrl(heroPath).then(url => { if (url) setHeroSignedUrl(url); });
    } else if (heroImageUrl) {
      setHeroSignedUrl(heroImageUrl);
    } else {
      setHeroSignedUrl("");
    }
  }, [heroPath, heroImageUrl, heroFile]);

  // Dedicated effect to sync hero aspect from DB
  useEffect(() => {
    if (existingContest?.contest) {
      const dbValue = existingContest.contest.hero_image_aspect;
      const normalized = normalizeHeroAspect(dbValue);
      console.log("Syncing hero aspect from DB:", { dbValue, normalized });
      setHeroImageAspect(normalized);
    }
  }, [existingContest]);

  // Debug logger for state changes
  useEffect(() => {
    console.log("heroImageAspect state updated:", heroImageAspect);
  }, [heroImageAspect]);

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleNameChange = (value: string) => {
    setName(value);
    // Auto-generate slug from name only if NOT editing (usually) and NOT free plan
    // If Editing, we generally don't change slug when name changes, unless they clear it?
    // Current logic: if slug matches generated name, keep in sync.
    // Enhanced: Only if !isFreePlan.
    if (!isFreePlan && !isEditing && (!slug || slug === generateSlug(name))) {
      setSlug(generateSlug(value));
    }
  };

  const addEntryMethod = () => {
    setEntryMethods([
      ...entryMethods,
      {
        key: "",
        label: "",
        description: "",
        entriesAwarded: 1,
        actionUrl: "",
        iconType: "library",
        iconKey: "default",
        iconUrl: null,
        isEnabled: true,
      },
    ]);
  };

  const removeEntryMethod = (index: number) => {
    setEntryMethods(entryMethods.filter((_, i) => i !== index));
  };

  const handleFileSelect = (file: File, type: 'logo' | 'hero') => {
    // Basic size check (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image under 5MB.",
        variant: "destructive"
      });
      return;
    }

    const url = URL.createObjectURL(file);

    if (type === 'logo') {
      setLogoFile(file);
      setLogoPath(""); // Clear saved path to favor new file
      setLogoUrl("");  // Clear legacy
      setLogoSignedUrl(url);
    } else {
      setHeroFile(file);
      setHeroPath("");
      setHeroImageUrl("");
      setHeroSignedUrl(url);
    }
  };

  const updateEntryMethod = (index: number, field: keyof EntryMethodInput, value: string | number | boolean | null) => {
    const updated = [...entryMethods];
    updated[index] = { ...updated[index], [field]: value };

    // New methods take a key from the label. Saved methods keep their key so a
    // label edit updates the same row instead of inserting a second one.
    if (field === "label" && typeof value === "string" && !updated[index].id) {
      const base = generateSlug(value);
      if (base) {
        const taken = new Set(updated.filter((_, itemIndex) => itemIndex !== index).map((method) => method.key));
        let key = base;
        let suffix = 2;
        while (taken.has(key)) {
          key = `${base}-${suffix}`;
          suffix += 1;
        }
        updated[index].key = key;
      }
    }

    setEntryMethods(updated);
  };

  const fieldOrderLabel = (field: ContestEntryField) => {
    if (field.field_type === "custom") return field.label.trim() || "Custom field";
    const labels: Record<string, string> = {
      name: "Full Name",
      phone: "Phone Number",
      address: "Mailing Address",
      email_opt_in: "Email Subscription",
      sms_opt_in: "SMS Subscription",
    };
    return labels[field.field_key] || field.label;
  };

  const moveEntryField = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= entryFields.length) return;
    const updated = [...entryFields];
    [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
    setEntryFields(updated.map((field, sortOrder) => ({ ...field, sort_order: sortOrder })));
  };

  const handleEntryIconSelect = (index: number, file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 2MB for icons", variant: "destructive" });
      return;
    }

    const url = URL.createObjectURL(file);
    const updated = [...entryMethods];
    updated[index] = {
      ...updated[index],
      iconFile: file,
      iconUrl: url,
      iconType: 'upload'
    };
    setEntryMethods(updated);
  };

  const updateTheme = (field: keyof ContestTheme, value: string) => {
    setTheme(prev => ({ ...prev, [field]: value }));
  };

  const updateTermsField = (field: keyof TermsFieldsData, value: string) => {
    setTermsFields(prev => ({ ...prev, [field]: value }));
  };

  const processContestSave = async () => {
    setIsSaving(true);
    setSubmittingStatus("creating");
    let finalNavSlug = slug;

    // Validate End Date
    let endsAtString: string | null = null;
    let endsAtDate: Date | null = null;
    if (endDate) {
      const dt = new Date(endDate);
      const [h, m] = endTime.split(':').map(Number);
      dt.setHours(h || 23, m || 59);
      endsAtString = dt.toISOString();
      endsAtDate = dt;
    }

    // Validate Help Email
    if (showHelpEmail) {
      if (!helpEmail.trim()) {
        setHelpEmailError("Required");
        toast({ title: "Help Email Required", description: "Please enter a help email address.", variant: "destructive" });
        setIsSaving(false);
        setSubmittingStatus("idle");
        return;
      }
      // Simple format check
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(helpEmail.trim())) {
        setHelpEmailError("Invalid email");
        toast({ title: "Invalid Help Email", description: "Please enter a valid email address.", variant: "destructive" });
        setIsSaving(false);
        setSubmittingStatus("idle");
        return;
      }
    } else {
      setHelpEmailError("");
    }

    const entryFieldError = validateEntryFieldsForSave(entryFields);
    if (entryFieldError) {
      toast({ title: "Entry fields", description: entryFieldError, variant: "destructive" });
      setIsSaving(false);
      setSubmittingStatus("idle");
      return;
    }

    if (!endsAtString || !endsAtDate) {
      toast({ title: "End Date Required", description: "Please select an end date and time.", variant: "destructive" });
      setIsSaving(false);
      setSubmittingStatus("idle");
      return;
    }

    // Enforce 5 minute buffer
    const now = new Date();
    const fiveMinsFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    if (endsAtDate < fiveMinsFromNow) {
      toast({
        title: "Invalid End Time",
        description: "End time must be at least 5 minutes in the future.",
        variant: "destructive"
      });
      setIsSaving(false);
      setSubmittingStatus("idle");
      return;
    }

    try {
      let contestId = existingContest?.contest?.id;
      let orgId = org?.id;

      // 1. Create Contest (Fast Phase A)
      if (!isEditing) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Please sign in again");

        const methodsDataForCreate = entryMethods.map((m, i) => ({
          key: m.key,
          label: m.label,
          description: m.description || null,
          entries_awarded: m.entriesAwarded,
          action_url: m.actionUrl || null,
          icon_type: m.iconType,
          icon_key: m.iconKey,
          icon_url: m.iconUrl,
          is_enabled: m.isEnabled,
          sort_order: i,
        }));

        const { data, error } = await supabase.functions.invoke("admin-create-contest", {
          body: {
            name,
            slug,
            description: null,
            referralBonusEntries: referralBonus,
            entryMethods: methodsDataForCreate,
            endsAt: endsAtString,
            theme,
            terms: termsFields
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (error || !data?.ok) {
          throw new Error(error?.message || data?.error || "Failed to create contest");
        }

        contestId = data.contest.id;
        finalNavSlug = data.contest.slug;
        orgId = data.contest.organization_id;

        const { error: createSettingsError } = await supabase.from("contests").update({
          description_html: descriptionHtml || null,
          hero_image_aspect: normalizeHeroAspect(heroImageAspect),
          use_org_logo: useOrgLogo,
          hide_powered_by_branding: hidePoweredByBranding,
          use_custom_terms: useCustomTerms,
          custom_terms_text: useCustomTerms ? customTermsText : null,
          require_terms_acceptance: requireTermsAcceptance,
          show_help_email: showHelpEmail,
          help_email: helpEmail.trim() || null,
          show_total_entries: showTotalEntries,
          initial_form_counts_as_entry: initialFormCountsAsEntry,
          show_entry_methods_heading: showEntryMethodsHeading,
          entry_methods_heading: entryMethodsHeading.trim() || DEFAULT_ENTRY_METHODS_HEADING,
          ...(useOrgLogo ? { logo_path: null } : {}),
        } as any).eq("id", contestId);
        if (createSettingsError) throw createSettingsError;

        await persistEntryMethodsHeading(
          supabase,
          contestId,
          showEntryMethodsHeading,
          entryMethodsHeading,
        );
        await persistContestEntryFields(supabase, contestId, entryFields);

        toast({ title: "Contest Created!", description: "Finalizing assets in background...", duration: 3000 });
        setSubmittingStatus("redirecting");
        navigate(`/admin/contests/${finalNavSlug}`);

        (async () => {
          try {
            const uploadPromises = [];

            if (logoFile) {
              const ext = normalizeImageFileExt(logoFile.name);
              const path = `${orgId}/contests/${contestId}/logo/logo.${ext}`;
              const p = await uploadOrgAsset(logoFile, path);
              uploadPromises.push(supabase.from("contests").update({ logo_path: p } as any).eq("id", contestId));
            }

            if (heroFile) {
              const ext = normalizeImageFileExt(heroFile.name);
              const path = `${orgId}/contests/${contestId}/hero/hero.${ext}`;
              const p = await uploadOrgAsset(heroFile, path);
              uploadPromises.push(supabase.from("contests").update({ hero_image_path: p } as any).eq("id", contestId));
            }

            const { data: createdMethods } = await supabase
              .from("entry_methods")
              .select("id, key")
              .eq("contest_id", contestId);

            if (createdMethods) {
              const methodMap = new Map(createdMethods.map(m => [m.key, m.id]));
              for (const m of entryMethods) {
                if (m.iconFile && methodMap.has(m.key)) {
                  const mId = methodMap.get(m.key);
                  const ext = normalizeImageFileExt(m.iconFile.name);
                  const path = `${orgId}/contests/${contestId}/entry-methods/${mId}/icon.${ext}`;
                  uploadPromises.push(
                    uploadOrgAsset(m.iconFile, path).then(p =>
                      supabase.from("entry_methods").update({ icon_path: p } as any).eq("id", mId)
                    )
                  );
                }
              }
            }

            await Promise.all(uploadPromises);
            toast({ title: "Assets Uploaded", description: "Contest setup complete.", duration: 3000 });
          } catch (bgErr) {
            console.error("Background upload error", bgErr);
            toast({
              title: "Upload Warning",
              description: "Some assets failed to upload. Please check the contest settings.",
              variant: "destructive"
            });
          }
        })();

        return;
      }

      // 2. Edit Mode (Blocking Update)
      if (!contestId || !orgId) throw new Error("Missing ID");

      // Upload Assets
      let finalLogoPath = logoPath;
      if (logoFile) {
        const ext = normalizeImageFileExt(logoFile.name);
        const path = `${orgId}/contests/${contestId}/logo/logo.${ext}`;
        finalLogoPath = await uploadOrgAsset(logoFile, path);
      }

      let finalHeroPath = heroPath;
      if (heroFile) {
        const ext = normalizeImageFileExt(heroFile.name);
        const path = `${orgId}/contests/${contestId}/hero/hero.${ext}`;
        finalHeroPath = await uploadOrgAsset(heroFile, path);
      }

      // Update Contest
      const updatePayload: any = {
        name,
        slug,
        description_html: descriptionHtml || null,
        referral_bonus_entries: referralBonus,
        ends_at: endsAtString,
        theme_json: theme as Json,
        hero_image_aspect: normalizeHeroAspect(heroImageAspect),
        terms_variables_json: termsFields as Json,
        is_active: true,
        logo_path: useOrgLogo ? null : finalLogoPath,
        hero_image_path: finalHeroPath,
        use_org_logo: useOrgLogo,
        hide_powered_by_branding: hidePoweredByBranding,
        use_custom_terms: useCustomTerms,
        custom_terms_text: useCustomTerms ? customTermsText : null,
        require_terms_acceptance: requireTermsAcceptance,
        show_help_email: showHelpEmail,
        help_email: helpEmail.trim() || null,
        show_total_entries: showTotalEntries,
        initial_form_counts_as_entry: initialFormCountsAsEntry,
        show_entry_methods_heading: showEntryMethodsHeading,
        entry_methods_heading: entryMethodsHeading.trim() || DEFAULT_ENTRY_METHODS_HEADING,
      };

      const { error: updateError } = await supabase
        .from("contests")
        .update(updatePayload)
        .eq("id", contestId);

      if (updateError) {
        if (updateError.code === '23505' && updateError.message?.includes('slug')) {
          setSlug(`${slug}-${Math.floor(1000 + Math.random() * 9000)}`);
          throw new Error("Slug taken. Modified slug, please save again.");
        }
        throw updateError;
      }

      // Update Entry Methods (Delete & Insert logic for full sync)
      const methodsData = entryMethods.map((m, i) => ({
        contest_id: contestId,
        key: m.key,
        label: m.label,
        description: m.description || null,
        entries_awarded: m.entriesAwarded,
        action_url: m.actionUrl || null,
        icon_type: m.iconType,
        icon_key: m.iconKey,
        icon_url: m.iconType === 'upload' ? null : m.iconUrl,
        icon_path: m.iconPath,
        is_enabled: m.isEnabled,
        sort_order: i,
      }));

      // 1. Upsert (Update existing or Insert new)
      const { data: inserted, error: methodsError } = await supabase
        .from("entry_methods")
        .upsert(methodsData as any, { onConflict: 'contest_id,key' })
        .select();

      if (methodsError) throw methodsError;

      // 2. Remove methods that are no longer in the form. Rows people have
      // already used are hidden instead of deleted, so their entries stay put.
      const currentKeys = new Set(methodsData.map(m => m.key));

      const { data: allMethods, error: listMethodsError } = await supabase
        .from("entry_methods")
        .select("id, key")
        .eq("contest_id", contestId);

      if (listMethodsError) throw listMethodsError;

      const staleIds = (allMethods || [])
        .filter((method) => !currentKeys.has(method.key))
        .map((method) => method.id);

      if (staleIds.length > 0) {
        const { data: usedRows, error: usedError } = await supabase
          .from("entries")
          .select("entry_method_id")
          .in("entry_method_id", staleIds);

        if (usedError) throw usedError;

        const usedIds = new Set((usedRows || []).map((row) => row.entry_method_id).filter(Boolean));
        const unusedIds = staleIds.filter((id) => !usedIds.has(id));
        const usedIdList = staleIds.filter((id) => usedIds.has(id));

        if (unusedIds.length > 0) {
          const { error: deleteError } = await supabase
            .from("entry_methods")
            .delete()
            .in("id", unusedIds);

          if (deleteError) {
            const { error: disableError } = await supabase
              .from("entry_methods")
              .update({ is_enabled: false })
              .in("id", unusedIds);
            if (disableError) throw deleteError;
          }
        }

        if (usedIdList.length > 0) {
          const { error: disableError } = await supabase
            .from("entry_methods")
            .update({ is_enabled: false })
            .in("id", usedIdList);
          if (disableError) throw disableError;
        }
      }

      if (methodsError) throw methodsError;

      // Handle Icon Uploads for Edited Methods
      if (inserted && inserted.length > 0) {
        const dbMap = new Map(inserted.map(m => [m.key, m]));
        for (const mState of entryMethods) {
          const mDb = dbMap.get(mState.key);
          if (mDb && mState.iconFile) {
            const ext = normalizeImageFileExt(mState.iconFile.name);
            const path = `${orgId}/contests/${contestId}/entry-methods/${mDb.id}/icon.${ext}`;
            await uploadOrgAsset(mState.iconFile, path);
            await supabase.from("entry_methods").update({ icon_path: path } as any).eq("id", mDb.id);
          }
        }
      }

      await persistEntryMethodsHeading(
        supabase,
        contestId,
        showEntryMethodsHeading,
        entryMethodsHeading,
      );
      await persistContestEntryFields(supabase, contestId, entryFields);

      await queryClient.invalidateQueries({ queryKey: ["admin-contest-edit", slug] });
      toast({
        title: "Contest Updated!",
        description: `Successfully saved contest.`,
      });
      navigate(`/admin/contests/${slug}`);

    } catch (err: any) {
      console.error("Error saving contest:", err);
      toast({
        title: "Error",
        description: err.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check Terms Completeness
    let isComplete = true;

    if (useCustomTerms) {
      if (!customTermsText.trim()) isComplete = false;
    } else {
      const required: (keyof TermsFieldsData)[] = [
        'sponsorName', 'sponsorAddress', 'eligibleRegions',
        'prizeDescription', 'governingState', 'supportEmail'
      ];

      // 1. Check Empty Required
      if (required.some(key => !termsFields[key]?.trim())) {
        isComplete = false;
      }

      // 2. Check Placeholders
      const placeholders = [
        "[Sponsor Name]", "[Sponsor Address]", "[Eligible Regions]",
        "SPONSOR_NAME", "SPONSOR_ADDRESS", "ELIGIBLE_COUNTRIES_OR_REGIONS", "MINIMUM_AGE",
        "START_DATE", "START_TIME", "PRIZE_DESCRIPTION", "PRIZE_ARV",
        "WINNER_NOTIFICATION_METHOD", "WINNER_NOTIFICATION_TIMEFRAME",
        "RESPONSE_WINDOW", "GOVERNING_STATE", "SUPPORT_EMAIL"
      ];

      const values = Object.values(termsFields);
      // Check for exact placeholders or bracketed placeholders
      if (values.some(val => typeof val === 'string' && placeholders.some(ph => val.includes(ph)))) {
        isComplete = false;
      }
    }

    if (!isComplete) {
      setShowIncompleteTermsModal(true);
      return;
    }

    processContestSave();
  };

  if (isCheckingAuth || (isEditing && isLoadingContest) || isLoadingOrg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
        </div>
      </header>

      <main className="container max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-heading text-2xl font-bold mb-8">
          {isEditing ? "Edit Contest" : "Create New Contest"}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic info */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-heading font-bold mb-4">Contest Details</h2>

            <div className="space-y-2">
              <Label htmlFor="name">Contest Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Summer Giveaway 2024"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">
                URL Slug
                {isFreePlan ? " (Auto-generated)" : " *"}
              </Label>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">/c/</span>
                  <Input
                    id="slug"
                    value={(!isEditing && isFreePlan) ? "rk-xxxxxxxxxx" : slug}
                    onChange={(e) => setSlug(generateSlug(e.target.value))}
                    placeholder="summer-giveaway-2024"
                    required
                    disabled={isFreePlan}
                    className={cn(isFreePlan && "text-muted-foreground cursor-not-allowed bg-muted")}
                  />
                </div>
                {!isFreePlan && slug && (
                  <div className="text-xs min-h-[1.5em] flex items-center pl-8 relative">
                    <span
                      className="text-muted-foreground flex items-center gap-1 absolute transition-opacity duration-150"
                      style={{ opacity: slugStatus === 'checking' ? 1 : 0, pointerEvents: 'none' }}
                    >
                      <Loader2 className="w-3 h-3 animate-spin" /> Checking availability...
                    </span>
                    <span
                      className="text-green-600 flex items-center gap-1 absolute transition-opacity duration-150"
                      style={{ opacity: slugStatus === 'available' ? 1 : 0, pointerEvents: 'none' }}
                    >
                      <Check className="w-3 h-3" /> Available
                    </span>
                    <span
                      className="text-amber-600 flex items-center gap-1 absolute transition-opacity duration-150"
                      style={{ opacity: slugStatus === 'taken' ? 1 : 0, pointerEvents: 'none' }}
                    >
                      <X className="w-3 h-3" /> Taken
                    </span>
                  </div>
                )}
                <p className="text-[0.8rem] text-muted-foreground">
                  {isFreePlan
                    ? "Custom slugs are available on Starter and Growth plans."
                    : "Unique URL for your contest."}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description (Rich Text)</Label>
              <RichTextEditor
                key={isFormInitialized ? "initialized" : "loading"}
                content={descriptionHtml}
                onChange={setDescriptionHtml}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="referralBonus">Referral Bonus Entries</Label>
                <Input
                  id="referralBonus"
                  type="number"
                  min={0}
                  max={10}
                  value={referralBonus}
                  onChange={(e) => setReferralBonus(parseInt(e.target.value) || 0)}
                />
              </div>

              <div className="space-y-2">
                <Label>End Date & Time * <span className="text-xs font-normal text-muted-foreground ml-1">(Pacific Time)</span></Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-[140px]"
                    required
                  />
                </div>
              </div>
            </div>
            {/* Show Total Entries Config (Moved from Terms) */}
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="showTotalEntries" className="text-base">
                    Show Total Entries Count
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Displays "Total entries: X" on the public contest page.
                  </p>
                </div>
                <Switch
                  id="showTotalEntries"
                  checked={showTotalEntries}
                  onCheckedChange={setShowTotalEntries}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="initialFormCountsAsEntry" className="text-base">
                    Count initial form completion as 1 entry
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, each participant automatically receives 1 entry upon submitting the form.
                  </p>
                </div>
                <Switch
                  id="initialFormCountsAsEntry"
                  checked={initialFormCountsAsEntry}
                  onCheckedChange={setInitialFormCountsAsEntry}
                />
              </div>
            </div>
          </div>

          {/* Theme & Branding */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-heading font-bold mb-4">Theme & Branding</h2>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Heading Color</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={theme.headingColor}
                    onChange={(e) => updateTheme("headingColor", e.target.value)}
                    style={{
                      width: 36,
                      height: 36,
                      padding: 2,
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: "none",
                      flexShrink: 0,
                    }}
                  />
                  <Input
                    value={theme.headingColor}
                    onChange={(e) => updateTheme("headingColor", e.target.value)}
                    placeholder="#000000"
                    className="flex-1 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Button Background</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={theme.buttonBackground}
                    onChange={(e) => updateTheme("buttonBackground", e.target.value)}
                    style={{
                      width: 36,
                      height: 36,
                      padding: 2,
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: "none",
                      flexShrink: 0,
                    }}
                  />
                  <Input
                    value={theme.buttonBackground}
                    onChange={(e) => updateTheme("buttonBackground", e.target.value)}
                    placeholder="#000000"
                    className="flex-1 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Button Text</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={theme.buttonText}
                    onChange={(e) => updateTheme("buttonText", e.target.value)}
                    style={{
                      width: 36,
                      height: 36,
                      padding: 2,
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: "none",
                      flexShrink: 0,
                    }}
                  />
                  <Input
                    value={theme.buttonText}
                    onChange={(e) => updateTheme("buttonText", e.target.value)}
                    placeholder="#000000"
                    className="flex-1 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Page Background</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={theme.background}
                    onChange={(e) => updateTheme("background", e.target.value)}
                    style={{
                      width: 36,
                      height: 36,
                      padding: 2,
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: "none",
                      flexShrink: 0,
                    }}
                  />
                  <Input
                    value={theme.background}
                    onChange={(e) => updateTheme("background", e.target.value)}
                    placeholder="#000000"
                    className="flex-1 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6 pt-2">
              {/* Logo Section */}
              <div className="space-y-4 border-t border-b border-border py-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Contest Logo</Label>
                    <p className="text-xs text-muted-foreground">Displayed at the top of your contest page.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={useOrgLogo}
                      onCheckedChange={setUseOrgLogo}
                      id="use-org-logo"
                    />
                    <Label htmlFor="use-org-logo" className="font-normal cursor-pointer text-sm">
                      Use Organization Default
                    </Label>
                  </div>
                </div>

                {useOrgLogo ? (
                  <div className="p-4 bg-muted/30 rounded-lg flex items-center justify-center border border-dashed border-border min-h-[120px]">
                    {orgLogoSignedUrl ? (
                      <img src={orgLogoSignedUrl} alt="Org Logo" className="max-h-24 max-w-full object-contain" />
                    ) : (
                      <span className="text-muted-foreground text-sm">No organization logo set.</span>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-4">
                      {logoSignedUrl && (
                        <div className="relative group shrink-0">
                          <img src={logoSignedUrl} alt="Contest Logo" className="w-24 h-24 object-contain rounded-lg border bg-muted/50" />
                          <button type="button" onClick={() => { setLogoPath(""); setLogoUrl(""); setLogoFile(null); setLogoSignedUrl(""); }} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                            <XIcon className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="flex-1">
                        <Label
                          htmlFor="logo-upload"
                          className="flex flex-col items-center justify-center w-full h-24 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/5 hover:bg-muted/10 cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col items-center justify-center pt-2 pb-3">
                            <Upload className="w-5 h-5 mb-2 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground"><span className="font-semibold">Click to upload</span> logo</p>
                            <p className="text-[10px] text-muted-foreground/70">PNG, JPG, WebP (max 5MB)</p>
                          </div>
                          <input id="logo-upload" type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'logo')} />
                        </Label>
                      </div>
                    </div>
                    {/* Legacy URL fallback removed */}
                  </div>
                )}
              </div>

              {/* Hero Section */}
              <div className="space-y-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Hero Image</Label>
                  <p className="text-xs text-muted-foreground">The main banner image for your contest.</p>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-col md:flex-row gap-4">
                    {heroSignedUrl && (
                      <div className="relative group shrink-0 max-w-[240px]">
                        <img src={heroSignedUrl} alt="Hero" className="w-full h-auto object-cover rounded-lg border bg-muted/50 aspect-video" />
                        <button type="button" onClick={() => { setHeroPath(""); setHeroImageUrl(""); setHeroFile(null); setHeroSignedUrl(""); }} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                          <XIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex-1">
                      <Label
                        htmlFor="hero-upload"
                        className="flex flex-col items-center justify-center w-full h-32 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/5 hover:bg-muted/10 cursor-pointer transition-colors"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground"><span className="font-semibold">Click to upload</span> hero image</p>
                          <p className="text-xs text-muted-foreground/70">PNG, JPG, WebP (max 5MB)</p>
                        </div>
                        <input id="hero-upload" type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'hero')} />
                      </Label>
                    </div>
                  </div>
                  {/* Legacy URL fallback removed */}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="heroImageAspect">Hero Image Aspect Ratio</Label>
              <Select
                value={heroImageAspect}
                onValueChange={(value) => setHeroImageAspect(value as HeroAspect)}
              >
                <SelectTrigger id="heroImageAspect">
                  <SelectValue placeholder="Select aspect ratio" />
                </SelectTrigger>
                <SelectContent>
                  {HERO_ASPECT_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option === 'original' ? 'Original (no crop)' :
                        option} {option === '1:1' ? '(Square)' :
                          option === '16:9' ? '(Landscape)' :
                            option === '9:16' ? '(Portrait)' :
                              option === '4:5' ? '(Instagram Portrait)' :
                                option === '5:4' ? '(Classic Landscape)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hide Powered by Branding - Growth Plan Feature */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="hidePoweredByBranding">Hide "Powered by RaffleKit"</Label>
                  <p className="text-sm text-muted-foreground">
                    Remove branding from public contest page footer
                    {org?.plan_key !== 'growth' && (
                      <span className="ml-1 text-xs font-semibold text-accent">(Growth plan)</span>
                    )}
                  </p>
                </div>
                <Switch
                  id="hidePoweredByBranding"
                  checked={hidePoweredByBranding}
                  onCheckedChange={(checked) => {
                    if (org?.plan_key === 'growth') {
                      setHidePoweredByBranding(checked);
                    } else {
                      // Non-Growth plan: show upgrade toast
                      toast({
                        title: "Available on Growth plan",
                        description: "Upgrade to remove branding from your contests.",
                        action: (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/admin/settings?tab=billing')}
                          >
                            Upgrade
                          </Button>
                        ),
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Entry Fields */}
          <div className="bg-card rounded-xl border border-border p-6">
            <Accordion type="single" collapsible defaultValue={entryFields.length > 0 ? "entry-fields" : undefined}>
              <AccordionItem value="entry-fields" className="border-none">
                <AccordionTrigger className="hover:no-underline py-0 mb-6 group">
                  <div className="flex flex-col text-left">
                    <h2 className="font-heading font-bold text-lg group-hover:text-primary transition-colors">
                      Entry Fields
                      {isFreePlan && <span className="ml-2 text-xs font-semibold text-accent">(Starter plan)</span>}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 font-normal">
                      Collect additional information from participants when they enter.
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  <div className="space-y-4 border-b border-border/50 pb-6 mb-6">
                    {/* Locked Email Row */}
                    <div className="flex items-center justify-between pb-4 border-b border-border/30">
                      <div className="space-y-0.5 opacity-80">
                        <Label className="text-sm font-medium">Email Address</Label>
                        <p className="text-xs text-muted-foreground">Required to enter and participate in the contest</p>
                      </div>
                      <div className="flex items-center gap-4 opacity-80">
                        <div className="flex items-center space-x-2">
                          <Checkbox id="req-email" checked={true} disabled />
                          <Label htmlFor="req-email" className="text-sm font-normal text-muted-foreground cursor-not-allowed">Required</Label>
                        </div>
                        <Switch checked={true} disabled />
                      </div>
                    </div>

                    {[
                      { type: "name", key: "name", label: "Full Name", defaultLabel: "Full Name", desc: "Participant's full name" },
                      { type: "phone", key: "phone", label: "Phone Number", defaultLabel: "Phone Number", desc: "Phone number (e.g. for SMS follow-up)" },
                      { type: "address", key: "address", label: "Mailing Address", defaultLabel: "Mailing Address", desc: "Full mailing address (line 1, line 2, city, state, zip, country)" },
                      { type: "email_opt_in", key: "email_opt_in", label: "Email Subscription", defaultLabel: "Subscribe to email updates", desc: "Checkbox asking participant to opt in to email marketing" },
                      { type: "sms_opt_in", key: "sms_opt_in", label: "SMS Subscription", defaultLabel: "Subscribe to SMS updates", desc: "Checkbox asking participant to opt in to SMS marketing" },
                    ].map((fieldOption) => {
                      const existingIndex = entryFields.findIndex(f => f.field_key === fieldOption.key);
                      const isEnabled = existingIndex !== -1;
                      const existingField = isEnabled ? entryFields[existingIndex] : null;

                      const toggleField = (checked: boolean) => {
                        if (isFreePlan) {
                          toast({
                            title: "Available on Starter and Growth plans.",
                            description: "Upgrade to add more fields to your entry form.",
                            action: (
                              <Button variant="outline" size="sm" onClick={() => navigate('/admin/settings?tab=billing')}>
                                Upgrade
                              </Button>
                            ),
                          });
                          return;
                        }

                        if (checked) {
                          setEntryFields([
                            ...entryFields,
                            {
                              field_type: fieldOption.type as ContestEntryField["field_type"],
                              field_key: fieldOption.key,
                              label: fieldOption.defaultLabel,
                              is_required: false,
                              is_enabled: true,
                              sort_order: entryFields.length,
                              placeholder: "",
                              validation_rules: {}
                            }
                          ]);
                        } else {
                          setEntryFields(entryFields.filter(f => f.field_key !== fieldOption.key));
                        }
                      };

                      const toggleRequired = (checked: boolean) => {
                        if (!isEnabled) return;
                        const updated = [...entryFields];
                        updated[existingIndex].is_required = checked;
                        setEntryFields(updated);
                      };

                      const updateValidationRule = (ruleKey: string, ruleVal: any) => {
                        if (!isEnabled) return;
                        const updated = [...entryFields];
                        const rules = typeof updated[existingIndex].validation_rules === 'object' && updated[existingIndex].validation_rules !== null
                          ? { ...(updated[existingIndex].validation_rules as object) }
                          : {};
                        (rules as any)[ruleKey] = ruleVal;
                        updated[existingIndex].validation_rules = rules;
                        setEntryFields(updated);
                      };

                      return (
                        <div key={fieldOption.key} className="space-y-3 pt-2">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-medium">{fieldOption.label}</Label>
                              <p className="text-xs text-muted-foreground">{fieldOption.desc}</p>
                            </div>
                            <div className="flex items-center gap-4">
                              {isEnabled && (
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`req-${fieldOption.key}`}
                                    checked={existingField?.is_required || false}
                                    onCheckedChange={toggleRequired}
                                  />
                                  <Label htmlFor={`req-${fieldOption.key}`} className="text-sm font-normal cursor-pointer text-muted-foreground">
                                    Required
                                  </Label>
                                </div>
                              )}
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={toggleField}
                              />
                            </div>
                          </div>

                          {/* Sponsor URLs mapping stored in validation_rules */}
                          {isEnabled && (fieldOption.type === "email_opt_in" || fieldOption.type === "sms_opt_in") && (
                            <div className="pl-6 pt-2 space-y-3 border-l-2 border-primary/20 ml-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Privacy Policy URL (Optional)</Label>
                                <Input
                                  placeholder="https://example.com/privacy"
                                  className="h-8 text-sm"
                                  value={((existingField?.validation_rules as Record<string, any>)?.privacy_url as string) || ""}
                                  onChange={(e) => updateValidationRule("privacy_url", e.target.value)}
                                />
                              </div>
                              {fieldOption.type === "sms_opt_in" && (
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Terms of Service URL (Optional)</Label>
                                  <Input
                                    placeholder="https://example.com/terms"
                                    className="h-8 text-sm"
                                    value={((existingField?.validation_rules as Record<string, any>)?.terms_url as string) || ""}
                                    onChange={(e) => updateValidationRule("terms_url", e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Custom Fields */}
                  <div>
                    <h3 className="text-sm font-semibold mb-4 text-foreground">
                      Custom Fields
                      {(isFreePlan || org?.plan_key === 'starter') && (
                        <span className="ml-2 text-xs font-semibold text-accent">(Growth plan)</span>
                      )}
                    </h3>
                    <div className="space-y-4 mb-4">
                      {(() => {
                        const isCustomFieldsLocked = isFreePlan || org?.plan_key === 'starter';
                        const showCustomUpgradeToast = () => {
                          toast({
                            title: "Custom fields are available on the Growth plan.",
                            description: "Upgrade to add custom fields to your entry form.",
                            action: (
                              <Button variant="outline" size="sm" onClick={() => navigate('/admin/settings?tab=billing')}>
                                Upgrade
                              </Button>
                            ),
                          });
                        };

                        return (
                          <>
                            {entryFields.filter(f => f.field_type === "custom").map((field) => {
                              const actualIndex = entryFields.findIndex(f => f.field_key === field.field_key);

                              const updateCustomField = (key: keyof typeof field, val: any) => {
                                const updated = [...entryFields];
                                updated[actualIndex] = { ...updated[actualIndex], [key]: val };
                                setEntryFields(updated);
                              };

                              const deleteCustomField = () => {
                                setEntryFields(entryFields.filter(f => f.field_key !== field.field_key));
                              };

                              const handleDisabledClick = () => {
                                if (isCustomFieldsLocked) {
                                  showCustomUpgradeToast();
                                }
                              };

                              const updateCustomFieldValidation = (patch: Partial<FieldValidationRules>) => {
                                const updated = [...entryFields];
                                const rules: FieldValidationRules = {
                                  ...(updated[actualIndex].validation_rules || {}),
                                  ...patch,
                                };
                                updated[actualIndex].validation_rules = rules;
                                setEntryFields(updated);
                              };

                              const currentInputType = field.validation_rules?.input_type || "text";
                              const dropdownOptions = field.validation_rules?.options || [];
                              const dateMode = field.validation_rules?.date_mode || "full";
                              const dateRange = field.validation_rules?.date_range || "both";

                              return (
                                <div key={field.field_key} className="space-y-3 bg-muted/40 p-3 rounded-md border border-border/50">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 flex items-center space-x-2" onClickCapture={handleDisabledClick}>
                                      <Input
                                        placeholder="Question or Field Label"
                                        value={field.label}
                                        onChange={(e) => updateCustomField("label", e.target.value)}
                                        className={cn("h-8 flex-1", isCustomFieldsLocked && "pointer-events-none opacity-60")}
                                        required
                                        readOnly={isCustomFieldsLocked}
                                      />
                                      <Select
                                        disabled={isCustomFieldsLocked}
                                        value={currentInputType}
                                        onValueChange={(val) => {
                                          const next = val as CustomInputType;
                                          const rules: FieldValidationRules = {
                                            ...(field.validation_rules || {}),
                                            input_type: next,
                                          };
                                          if (next === "dropdown" && (rules.options || []).length < 2) {
                                            rules.options = ["", ""];
                                          }
                                          if (next === "date") {
                                            rules.date_mode = rules.date_mode || "full";
                                            rules.date_range = rules.date_range || "both";
                                          }
                                          updateCustomField("validation_rules", rules);
                                        }}
                                      >
                                        <SelectTrigger className="w-[140px] h-8 text-xs">
                                          <SelectValue placeholder="Type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="text">Text Input</SelectItem>
                                          <SelectItem value="checkbox">Checkbox</SelectItem>
                                          <SelectItem value="dropdown">Dropdown</SelectItem>
                                          <SelectItem value="date">Date</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center gap-2" onClickCapture={handleDisabledClick}>
                                      <Checkbox
                                        id={`req-${field.field_key}`}
                                        checked={field.is_required}
                                        onCheckedChange={(c) => {
                                          if (!isCustomFieldsLocked) {
                                            updateCustomField("is_required", c);
                                          }
                                        }}
                                        disabled={isCustomFieldsLocked}
                                      />
                                      <Label htmlFor={`req-${field.field_key}`} className="text-xs mr-2 cursor-pointer">
                                        Required
                                      </Label>
                                      {!isCustomFieldsLocked && (
                                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={deleteCustomField}>
                                          <X className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>

                                  {currentInputType === "dropdown" && (
                                    <div className="space-y-2 pl-1" onClickCapture={handleDisabledClick}>
                                      <Label className="text-xs text-muted-foreground">Dropdown options</Label>
                                      {dropdownOptions.map((option, optionIndex) => (
                                        <div key={`${field.field_key}-opt-${optionIndex}`} className="flex items-center gap-2">
                                          <Input
                                            value={option}
                                            placeholder={`Option ${optionIndex + 1}`}
                                            className={cn("h-8", isCustomFieldsLocked && "pointer-events-none opacity-60")}
                                            readOnly={isCustomFieldsLocked}
                                            onChange={(e) => {
                                              const next = [...dropdownOptions];
                                              next[optionIndex] = e.target.value;
                                              updateCustomFieldValidation({ options: next });
                                            }}
                                          />
                                          {!isCustomFieldsLocked && dropdownOptions.length > 2 && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-muted-foreground"
                                              onClick={() => {
                                                updateCustomFieldValidation({
                                                  options: dropdownOptions.filter((_, i) => i !== optionIndex),
                                                });
                                              }}
                                            >
                                              <X className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      ))}
                                      {!isCustomFieldsLocked && (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-8"
                                          onClick={() => updateCustomFieldValidation({ options: [...dropdownOptions, ""] })}
                                        >
                                          <Plus className="w-3 h-3 mr-1" />
                                          Add option
                                        </Button>
                                      )}
                                    </div>
                                  )}

                                  {currentInputType === "date" && (
                                    <div className="grid gap-3 sm:grid-cols-2 pl-1" onClickCapture={handleDisabledClick}>
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Date format</Label>
                                        <Select
                                          disabled={isCustomFieldsLocked}
                                          value={dateMode}
                                          onValueChange={(val) => updateCustomFieldValidation({ date_mode: val as "full" | "month_year" })}
                                        >
                                          <SelectTrigger className="h-8 text-xs">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="full">Month, day, and year</SelectItem>
                                            <SelectItem value="month_year">Month and year only</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Allowed dates</Label>
                                        <Select
                                          disabled={isCustomFieldsLocked}
                                          value={dateRange}
                                          onValueChange={(val) => updateCustomFieldValidation({ date_range: val as DateRange })}
                                        >
                                          <SelectTrigger className="h-8 text-xs">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="future">Future only</SelectItem>
                                            <SelectItem value="past">Past only</SelectItem>
                                            <SelectItem value="both">Past and future</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                if (isCustomFieldsLocked) {
                                  e.preventDefault();
                                  showCustomUpgradeToast();
                                  return;
                                }
                                setEntryFields([
                                  ...entryFields,
                                  {
                                    field_type: "custom",
                                    field_key: `custom_${Date.now()}`,
                                    label: "",
                                    is_required: false,
                                    is_enabled: true,
                                    sort_order: entryFields.length,
                                    placeholder: "",
                                    validation_rules: { input_type: "text" }
                                  }
                                ]);
                              }}
                              className={cn(isCustomFieldsLocked && "opacity-50")}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Custom Field
                            </Button>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {entryFields.length > 0 && (
                    <div className="border-t border-border/50 pt-6">
                      <h3 className="text-sm font-semibold mb-1">Field order</h3>
                      <p className="text-xs text-muted-foreground mb-3">
                        Email is always first. Reorder the other fields as they should appear on the contest page.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                          <span className="text-sm">Email Address</span>
                          <span className="text-xs text-muted-foreground">Always first</span>
                        </div>
                        {entryFields.map((field, index) => (
                          <div
                            key={field.field_key}
                            className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/40 px-3 py-2"
                          >
                            <span className="text-sm truncate">{fieldOrderLabel(field)}</span>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                disabled={index === 0}
                                onClick={() => moveEntryField(index, -1)}
                                aria-label={`Move ${fieldOrderLabel(field)} up`}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                disabled={index === entryFields.length - 1}
                                onClick={() => moveEntryField(index, 1)}
                                aria-label={`Move ${fieldOrderLabel(field)} down`}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Entry methods */}
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold">Entry Methods</h2>
              <Button type="button" variant="outline" size="sm" onClick={addEntryMethod}>
                <Plus className="w-4 h-4 mr-1" />
                Add Method
              </Button>
            </div>

            <div className="rounded-md border border-border/60 p-4 mb-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="showEntryMethodsHeading" className="text-sm font-medium">
                    Show heading above methods
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Appears on the public contest page after someone enters.
                  </p>
                </div>
                <Switch
                  id="showEntryMethodsHeading"
                  checked={showEntryMethodsHeading}
                  onCheckedChange={(checked) => {
                    setShowEntryMethodsHeading(checked);
                    if (checked && !entryMethodsHeading.trim()) {
                      setEntryMethodsHeading(DEFAULT_ENTRY_METHODS_HEADING);
                    }
                  }}
                />
              </div>
              {showEntryMethodsHeading && (
                <div className="space-y-1">
                  <Label htmlFor="entryMethodsHeading" className="text-xs text-muted-foreground">Heading text</Label>
                  <Input
                    id="entryMethodsHeading"
                    value={entryMethodsHeading}
                    onChange={(e) => setEntryMethodsHeading(e.target.value)}
                    placeholder={DEFAULT_ENTRY_METHODS_HEADING}
                    className="h-8"
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              {entryMethods.map((method, index) =>
                method.key !== 'signup' ? (
                  <EntryMethodEditor
                    key={method.id || `${method.key}-${index}`}
                    method={method}
                    index={index}
                    canDelete={entryMethods.filter(m => m.key !== 'signup').length > 1}
                    onChange={updateEntryMethod}
                    onFileSelect={handleEntryIconSelect}
                    onDelete={removeEntryMethod}
                  />
                ) : null
              )}
            </div>
          </div>

          {/* Help & Support */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-heading font-bold mb-4">Help & Support</h2>

            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="showHelpEmail" className="text-base">
                    Show Help Email Link
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Displays a "Help" link in the contest footer
                  </p>
                </div>
                <Switch
                  id="showHelpEmail"
                  checked={showHelpEmail}
                  onCheckedChange={setShowHelpEmail}
                />
              </div>

              {showHelpEmail && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                  <Label htmlFor="helpEmail">Help Email Address</Label>
                  <Input
                    id="helpEmail"
                    value={helpEmail}
                    onChange={(e) => {
                      setHelpEmail(e.target.value);
                      if (helpEmailError) setHelpEmailError("");
                    }}
                    placeholder="support@example.com"
                    className={helpEmailError ? "border-destructive" : ""}
                  />
                  {helpEmailError && (
                    <p className="text-sm text-destructive font-medium">{helpEmailError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Terms & Conditions Section */}
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading font-bold text-lg">Terms & Conditions</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure contest terms and legal requirements
                </p>
              </div>
              <div className="flex items-center gap-3">
                {!useCustomTerms && (
                  <TermsPreviewButton
                    termsFields={termsFields}
                    referralBonusEntries={referralBonus}
                    endsAt={endDate}
                    startsAt={undefined}
                  />
                )}
                <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
                <Label htmlFor="useCustomTerms" className="text-sm font-medium cursor-pointer">
                  Use custom Terms & Conditions
                </Label>
                <Switch
                  id="useCustomTerms"
                  checked={useCustomTerms}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      // Show warning modal before enabling
                      setShowCustomTermsWarning(true);
                    } else {
                      // Disable custom terms immediately
                      setUseCustomTerms(false);
                    }
                  }}
                />
              </div>
            </div>


            <div className="flex items-center justify-between py-4 border-b border-border/50 mb-6">
              <div className="space-y-0.5">
                <Label htmlFor="requireTermsAcceptance" className="text-sm font-medium">
                  Require entrants to accept Terms before entering
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adds a mandatory checkbox on the email capture screen
                </p>
              </div>
              <Switch
                id="requireTermsAcceptance"
                checked={requireTermsAcceptance}
                onCheckedChange={setRequireTermsAcceptance}
              />
            </div>





            {useCustomTerms ? (
              // Custom Terms Mode
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                    ⚠️ You are using custom terms. Ensure they are legally reviewed.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customTermsText">Custom Terms & Conditions (Text/Markdown)</Label>
                  <Textarea
                    id="customTermsText"
                    value={customTermsText}
                    onChange={(e) => setCustomTermsText(e.target.value)}
                    placeholder="Enter your custom terms and conditions here..."
                    className="min-h-[400px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use Markdown for formatting. HTML is not supported and will render as text.
                  </p>
                </div>
              </div>
            ) : (
              // Template Mode
              <TermsFieldsAccordion
                values={termsFields}
                onChange={updateTermsField}
              />
            )}
          </div>

          {isEditing && existingContest?.contest?.id && org?.id && (
            <div className="mt-8">
              <LifecycleContestSettings organizationId={org.id} contestId={existingContest.contest.id} />
            </div>
          )}

          {/* Custom Terms Warning Modal */}
          <Dialog open={showCustomTermsWarning} onOpenChange={setShowCustomTermsWarning}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Use custom Terms & Conditions?</DialogTitle>
                <DialogDescription className="space-y-3 pt-2">
                  <p>
                    Unless reviewed by legal counsel, we highly advise using our template.
                  </p>
                  <p>
                    We are not attorneys and cannot guarantee legal compliance. We've done our best
                    to cover common requirements.
                  </p>
                  <p className="font-semibold text-foreground">
                    Only use custom terms if your legal counsel provided them.
                  </p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowCustomTermsWarning(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setUseCustomTerms(true);
                    setShowCustomTermsWarning(false);
                  }}
                >
                  I Understand—Use Custom
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showIncompleteTermsModal} onOpenChange={setShowIncompleteTermsModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Terms & Conditions look incomplete</DialogTitle>
                <DialogDescription className="pt-2">
                  You haven’t filled out all required Terms & Conditions fields. We recommend completing them before publishing.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowIncompleteTermsModal(false)}>
                  Keep editing
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowIncompleteTermsModal(false);
                    processContestSave();
                  }}
                >
                  Publish anyway
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="pb-6">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={isSaving || (!isEditing && !legalConfirmed)}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? "Saving..." :
                    submittingStatus === "redirecting" ? "Redirecting..." :
                      "Creating..."}
                </>
              ) : (
                isEditing ? "Save Changes" : "Create Contest"
              )}
            </Button>

            {!isEditing && (
              <div className="flex items-start space-x-2 mt-3">
                <Checkbox
                  id="legalConfirmed"
                  checked={legalConfirmed}
                  onCheckedChange={(checked) => setLegalConfirmed(checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="legalConfirmed"
                    className="text-sm text-foreground/80 font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I confirm this contest complies with applicable laws and that I have read the RaffleKit{" "}
                    <Link to="/terms" target="_blank" className="underline underline-offset-4 hover:text-primary">
                      Terms of Service
                    </Link>
                    .
                  </Label>
                </div>
              </div>
            )}
          </div>
        </form>
      </main>
    </div >
  );
}
